import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router'
import { RequireSession } from '../auth/RequireSession'
import { useAuthenticatedSession } from '../auth/session'
import { useT } from '../i18n'
import { Billers } from '../screens/billers/Billers'
import { FieldExposure } from '../screens/fieldexposure/FieldExposure'
import { Gateways } from '../screens/gateways/Gateways'
import { Landing } from '../screens/Landing'
import { Profile } from '../screens/profile/Profile'
import { Support } from '../screens/support/Support'
import { Transactions } from '../screens/transactions/Transactions'
import { parseFilters, toSearch, type FilterSearch, type Filters } from '../screens/transactions/filters'
import { Users } from '../screens/users/Users'
import { ShellWithBillers } from '../shell/ShellWithBillers'
import { Button, EmptyState } from '../ui'

/**
 * Code-based routes rather than the file-based generator: the route set is
 * small and fixed, and a build-time codegen step would have to be wired into
 * both the Vite build and the Docker image for no gain at this size.
 */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
})

function NotFound() {
  const t = useT()
  return (
    <EmptyState
      glyph="⌕"
      title={t('error.notFound')}
      actions={
        <Button onClick={() => window.location.assign('/transactions')}>
          {t('error.backToTransactions')}
        </Button>
      }
    />
  )
}

/** Everything under here requires a session. The redirect is a convenience,
 *  NOT a security boundary — the BFF rejects unauthenticated calls and the Go
 *  API re-checks the biller scope on every request. A client-side route guard
 *  can always be bypassed and must never be the only thing standing between a
 *  user and someone else's data. */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  return (
    <RequireSession>
      <ShellWithBillers>
        <Outlet />
      </ShellWithBillers>
    </RequireSession>
  )
}

/*
`/` is the portal's front door and normally just forwards to the transactions
list. It is ALSO where the BFF drops a caller whose sign-in it refused, as
`/?error=...`, and that message has to survive.

Forwarding regardless would lose it twice over: the redirect does not carry the
search string, and `/transactions` validates its own search down to the filter
keys, so an `error` would be stripped even if it were passed through. The
landing screen would then render with nothing to say and the user would be left
to guess whether they had mistyped their password. So a request carrying an
error renders the landing screen here instead, keeping the parameter in the URL
for Landing to read.
*/
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): { error?: string } =>
    typeof search['error'] === 'string' && search['error'] !== '' ? { error: search['error'] } : {},
  beforeLoad: ({ search }) => {
    if (search.error) return
    throw redirect({ to: '/transactions' })
  },
  component: Landing,
})

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signed-out',
  component: () => <Landing signedOut />,
})

/**
 * Filters live in the URL (plan D1), which is why this route declares
 * `validateSearch`. Everything is optional and every value is a string,
 * because a query string is user input — `parseFilters` is what turns it into
 * a valid filter set, and it is total by construction.
 */
const transactionsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/transactions',
  validateSearch: (search: Record<string, unknown>): FilterSearch => {
    const str = (key: string): string | undefined => {
      const value = search[key]
      return typeof value === 'string' && value !== '' ? value : undefined
    }
    const out: Record<string, string> = {}
    for (const key of ['from', 'to', 'status', 'min', 'max', 'ptn', 'gateway', 'biller', 'beneficiary', 'page', 'size', 'sort', 'dir']) {
      const value = str(key)
      if (value !== undefined) out[key] = value
    }
    return out as FilterSearch
  },
  component: TransactionsRoute,
})

function TransactionsRoute() {
  const search = transactionsRoute.useSearch()
  const navigate = transactionsRoute.useNavigate()
  const { businessTime } = useAuthenticatedSession()
  // One `now` per render, so the parse and the serialise agree on what "today"
  // is even if the render straddles midnight in Douala.
  const now = new Date()
  const filters = parseFilters(search, businessTime, now)

  const onFilters = (next: Filters) => {
    // `replace` for filter changes: a reconciliation session adjusts filters
    // dozens of times, and pushing each one makes the back button useless for
    // the thing it is actually wanted for — leaving the screen.
    void navigate({ search: toSearch(next, businessTime, now), replace: true })
  }

  return <Transactions filters={filters} onFilters={onFilters} now={now} />
}

const usersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/users',
  component: Users,
})

/** Platform administration. The nav hides these from biller users and the API
 *  refuses them, so there is no client-side guard here — a second, divergent
 *  copy of an authorisation rule is a liability, and the one that counts is
 *  the server's. */
const billersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/billers',
  component: Billers,
})

/** The Billers screen deep-links here (`?biller=CODE`) so "manage this
 *  biller's gateways" opens pre-filtered. Same tolerant handling as the
 *  transactions route: a query string is user input, so anything that is not
 *  a non-empty string is simply dropped. */
const gatewaysRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/gateways',
  validateSearch: (search: Record<string, unknown>): { biller?: string } => {
    const value = search['biller']
    return typeof value === 'string' && value !== '' ? { biller: value } : {}
  },
  component: GatewaysRoute,
})

function GatewaysRoute() {
  const { biller } = gatewaysRoute.useSearch()
  return <Gateways {...(biller !== undefined ? { initialBiller: biller } : {})} />
}

const fieldExposureRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/field-exposure',
  component: FieldExposure,
})

/** Biller users only — the nav hides it from internal staff and the API
 *  refuses them; same rationale as the admin screens above, mirrored. */
const supportRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/support',
  component: Support,
})

const profileRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/profile',
  component: Profile,
})

export const routeTree = rootRoute.addChildren([
  indexRoute,
  landingRoute,
  authenticatedRoute.addChildren([
    transactionsRoute,
    usersRoute,
    billersRoute,
    gatewaysRoute,
    fieldExposureRoute,
    supportRoute,
    profileRoute,
  ]),
])

/**
 * Search params are plain strings in, plain strings out.
 *
 * TanStack Router's default serialiser JSON-encodes values, so a filter set
 * came back as `?min=%221000000%22&status=%22FAILED%22`. It round-trips
 * correctly, which is why nothing broke — but the whole point of putting
 * filters in the URL (plan D1) is that the URL is something a person reads,
 * edits and pastes to a colleague, and a quoted, percent-encoded one is none
 * of those. Every value this app puts in the query string is already a
 * string, so JSON adds nothing but noise.
 *
 * Reading stays tolerant: a hand-typed `?status=FAILED` and a legacy
 * `?status=%22FAILED%22` both reach `parseFilters`, which validates whatever
 * arrives.
 */
export const parseSearch = (searchStr: string): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const [key, value] of new URLSearchParams(searchStr)) {
    // Tolerate a JSON-quoted value left over from an older link.
    out[key] = /^".*"$/.test(value) ? value.slice(1, -1) : value
  }
  return out
}

export const stringifySearch = (search: Record<string, unknown>): string => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

/** `history` is injectable so tests can drive the real router on a memory
 *  history. The alternative — stubbing window.location — breaks the browser
 *  history the router is built on, and the symptom is a blank render with no
 *  error, which is a bad afternoon. */
export const createAppRouter = (history?: RouterHistory) =>
  createRouter({ routeTree, parseSearch, stringifySearch, ...(history ? { history } : {}) })

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
